import React, { useEffect, useState } from 'react';
import '@mui/material';
import { Button, TextField, Autocomplete } from '@mui/material';
import Api from '../../../utils/Api';
import BadgeComponent from '../../PageProfil/BadgeComponent';
import './BadgeCreateForm.css';
import Loading from '../../Loading/LoadingComponent';
import ImageDropZone from '../../ImageDropZone/ImageDropZone';
import AdminSidebar from '../../layout/AdminSidebar/AdminSidebar';

const badgeDummy = {
    title: "",
    description: "",
    imagePath: null,
    category: null
};

/**
 * Fonction qui affiche et gère la création de badge
 * @param {Object} handleClose - Ferme une fenêtre
 * @param {Object} addBadge - Popup d'ajout de badge
 * @param {Object} errorBadge - Popup de modification de badge
 * @returns Le formulaire d'ajout de badge
 * @author Vincent Houle 
 * @author Philippe-Vu Beaulieu
 */
export default function BadgeCreateForm({ handleClose, addBadge, errorBadge }) {

    const [titleError, setTitleError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [badge, setBadge] = useState(badgeDummy);

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Api.get('/category/').then((response) => {
            setCategories(response.data.categories);
            setLoading(false);
        }).catch((_) => {
        });
    }, []);

    const handleFileSelected = (file) => {
        setImageFile(file);
        setBadge(prev => ({ ...prev, imagePath: URL.createObjectURL(file) }));
    };

    const handleImageDelete = () => {
        setImageFile(null);
        setBadge(prev => ({ ...prev, imagePath: null }));
    };

    const validateTitle = () => {
        setTitleError("");
        if (badge.title.length === 0) {
            setTitleError('Veuillez renseigner le titre');
            return false;
        }
        return true;
    };

    const validateDescription = () => {
        setDescriptionError('');
        if (badge.description.length === 0) {
            setDescriptionError('Veuillez renseigner la description');
            return false;
        }
        return true;
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (validateTitle() && validateDescription()) {
            const formData = new FormData();
            formData.append('title', badge.title);
            formData.append('description', badge.description);

            if (imageFile !== null) {
                formData.append('image', imageFile);
            }

            if (badge.category) {
                badge.category.id === undefined || formData.append('category_id', badge.category.id);
                badge.category.name === undefined || formData.append('category_name', badge.category.name);
            }

            Api.post('/badge', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
                .then((response) => {
                    addBadge(response.data?.id);
                    setBadge(badgeDummy);
                    handleClose();
                })
                .catch((_) => {
                    errorBadge('Erreur lors de la création du badge');
                });
        }
    };

    return (
        <div className="badge-create-layout">
            <div className="badge-create-main-content">
                <AdminSidebar />

                <div className="badge-create-form">
                    <h1 className="badge-create-form-title">Création d'un badge</h1>

                    <div className="badge-create-form-card">
                        <form className="create-badge" onSubmit={handleSubmit}>
                            <TextField
                                id="title"
                                name="title"
                                placeholder="Titre *"
                                variant="outlined"
                                value={badge.title}
                                onChange={(e) => {
                                    setBadge((prevState) => ({
                                        ...prevState,
                                        title: e.target.value
                                    }));
                                }}
                                onBlur={validateTitle}
                                error={titleError.length > 0}
                                helperText={titleError}
                                inputProps={{ maxLength: 45 }}
                                required
                                fullWidth
                            />

                            <TextField
                                id="description"
                                name="description"
                                placeholder="Descriptions *"
                                variant="outlined"
                                multiline
                                rows={3}
                                value={badge.description}
                                inputProps={{ maxLength: 255 }}
                                onChange={(e) => {
                                    setBadge((prevState) => ({
                                        ...prevState,
                                        description: e.target.value
                                    }));
                                }}
                                onBlur={validateDescription}
                                error={descriptionError.length > 0}
                                helperText={descriptionError}
                                required
                                fullWidth
                            />

                            <div className="badge-create-form-category-selector">
                                {loading ? <Loading /> :
                                    <Autocomplete
                                        value={badge.category}
                                        onChange={(_, newValue) => {
                                            setBadge(prevState => ({
                                                ...prevState,
                                                category: newValue
                                            }));
                                        }}
                                        options={categories}
                                        getOptionLabel={(category) => category.name}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => <TextField {...params} placeholder="Catégories" />}
                                    />
                                }
                            </div>

                            <div className="badge-create-form-button-field">
                                <ImageDropZone
                                    inputId="badge-create-image"
                                    onFileSelected={handleFileSelected}
                                    onDelete={handleImageDelete}
                                    hasImage={badge.imagePath !== null}
                                />
                            </div>

                            <div className="badge-create-form-button-submit">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    className="badge-create-primary-action"
                                    disableRipple
                                >
                                    CRÉER
                                </Button>

                                <Button
                                    variant="outlined"
                                    type="button"
                                    onClick={() => {
                                        setBadge(badgeDummy);
                                        handleClose();
                                    }}
                                    className="badge-create-secondary-action"
                                >
                                    ANNULER
                                </Button>
                            </div>
                        </form>

                        <div className="badge-create-form-preview">
                            <h2 className="badge-create-form-preview-title">Prévisualisation</h2>

                            <div className="badge-create-form-preview-content">
                                <div className="badge-create-form-preview-badge">
                                    <BadgeComponent badge={badge} showDetails={false} />

                                    <div className="badge-create-form-preview-badge-title">
                                        {badge.title || "Badge sans titre"}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}